import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
    ArticlesExample,
    CustomerData,
    EmbedLevel,
    PricesExample,
    resolveSyncedArticleIds,
    resolveSyncedPriceIds,
} from '@/containers/widget/embedScript';
import {
    PreviewColorScheme,
    PriceQuantitiesChangePayload,
    SelectedArticlesChangePayload,
    SetArticleQuantityCommand,
    SetPriceQuantityCommand,
    useWidgetPreviewSession,
    WidgetPreviewSession,
} from '@/containers/widget/useWidgetPreview';
import {
    appendLogEntry,
    describeCommand,
    describePriceQuantities,
    describeSelectedArticles,
    LogDirection,
    PreviewLogEntry,
} from './previewLog';
import { KnownQuantities, planPriceSyncCommands, planSyncCommands } from './syncedArticles';

export interface EmbedPreviewOptions {
    configurationId: string;
    companySitepath: string;
    embedScriptUrl: string;
    fitViewport: boolean;
    level: EmbedLevel;
    articlesExample: ArticlesExample;
    syncedArticleIds: number[];
    pricesExample: PricesExample;
    syncedPriceIds: number[];
}

export interface EmbedPreviewController {
    session: WidgetPreviewSession;
    scheme: PreviewColorScheme;
    setScheme: (scheme: PreviewColorScheme) => void;
    log: PreviewLogEntry[];
    clearLog: () => void;
    mountKey: string;
    resetWidget: () => void;
    sendArticleQuantity: (command: SetArticleQuantityCommand) => boolean;
    sendPriceQuantity: (command: SetPriceQuantityCommand) => boolean;
    sendCustomerData: (customerData: CustomerData) => boolean;
}

type ArticlesListener = (payload: SelectedArticlesChangePayload) => void;

type PricesListener = (payload: PriceQuantitiesChangePayload) => void;

type Recorder = (direction: LogDirection, text: string) => void;

export const useEmbedPreviewController = (options: EmbedPreviewOptions): EmbedPreviewController => {
    const { configurationId, level, articlesExample, pricesExample } = options;
    const [scheme, setScheme] = useState<PreviewColorScheme>('light');
    const [resetCount, setResetCount] = useState(0);
    const [log, setLog] = useState<PreviewLogEntry[]>([]);
    const knownRef = useRef<KnownQuantities>({});
    const knownPricesRef = useRef<KnownQuantities>({});
    const listenerRef = useRef<ArticlesListener>(() => undefined);
    const priceListenerRef = useRef<PricesListener>(() => undefined);

    useEffect(() => {
        knownRef.current = {};
        knownPricesRef.current = {};
    }, [level, configurationId, articlesExample, pricesExample, resetCount]);

    const resetWidget = useCallback(() => setResetCount((count) => count + 1), []);

    const record = useCallback<Recorder>((direction, text) => {
        setLog((previous) => appendLogEntry(previous, { at: new Date(), direction, text }));
    }, []);

    const clearLog = useCallback(() => setLog([]), []);

    const forwardToListener = useCallback<ArticlesListener>(
        (payload) => listenerRef.current(payload),
        []
    );

    const forwardPricesToListener = useCallback<PricesListener>(
        (payload) => priceListenerRef.current(payload),
        []
    );

    const session = useWidgetPreviewSession({
        source: { kind: 'stored', configurationId },
        companySitepath: options.companySitepath,
        embedScriptUrl: options.embedScriptUrl,
        colorScheme: scheme,
        fitViewport: options.fitViewport,
        onSelectedArticlesChange: level === 'articles' ? forwardToListener : undefined,
        onPriceQuantitiesChange: level === 'prices' ? forwardPricesToListener : undefined,
    });

    const sendArticleQuantity = useLoggedSend(
        'setArticleQuantity',
        session.sendArticleQuantity,
        record
    );
    const sendPriceQuantity = useLoggedSend('setPriceQuantity', session.sendPriceQuantity, record);
    const sendCustomerData = useLoggedSend('setCustomerData', session.sendCustomerData, record);

    listenerRef.current = (payload) => {
        record('in', describeSelectedArticles(payload));
        planAndSend(payload, options, knownRef, sendArticleQuantity);
    };

    priceListenerRef.current = (payload) => {
        record('in', describePriceQuantities(payload));
        planAndSendPrices(payload, options, knownPricesRef, sendPriceQuantity);
    };

    return {
        session,
        scheme,
        setScheme,
        log,
        clearLog,
        mountKey: buildMountKey(options, scheme, resetCount),
        resetWidget,
        sendArticleQuantity,
        sendPriceQuantity,
        sendCustomerData,
    };
};

function useLoggedSend<T>(
    verb: string,
    send: (payload: T) => boolean,
    record: Recorder
): (payload: T) => boolean {
    return useCallback(
        (payload: T) => {
            const isSent = send(payload);
            if (isSent) record('out', describeCommand(verb, payload));
            return isSent;
        },
        [verb, send, record]
    );
}

const planAndSend = (
    payload: SelectedArticlesChangePayload,
    options: EmbedPreviewOptions,
    knownRef: MutableRefObject<KnownQuantities>,
    send: (command: SetArticleQuantityCommand) => boolean
): void => {
    if (options.articlesExample !== 'synced') return;
    const plan = planSyncCommands(
        knownRef.current,
        payload.articles,
        resolveSyncedArticleIds(options.syncedArticleIds)
    );
    knownRef.current = plan.known;
    plan.commands.forEach(send);
};

const planAndSendPrices = (
    payload: PriceQuantitiesChangePayload,
    options: EmbedPreviewOptions,
    knownRef: MutableRefObject<KnownQuantities>,
    send: (command: SetPriceQuantityCommand) => boolean
): void => {
    if (options.pricesExample !== 'synced') return;
    const plan = planPriceSyncCommands(
        knownRef.current,
        payload.quantities,
        resolveSyncedPriceIds(options.syncedPriceIds)
    );
    knownRef.current = plan.known;
    plan.commands.forEach(send);
};

const buildMountKey = (
    options: EmbedPreviewOptions,
    scheme: PreviewColorScheme,
    resetCount: number
): string =>
    [
        resetCount,
        options.configurationId,
        scheme,
        options.fitViewport,
        options.level,
        options.articlesExample,
        options.pricesExample,
    ].join('|');
